import React from 'react';
import { Button } from '@/components/ui/Button';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/Table';
import { Pagination } from '@/components/ui/Pagination';
import { EmptyState } from '@/components/ui/EmptyState';
import { BlockedSignupLog } from '@/types';
import { formatDateTimeToKST } from '@/utils/dateUtils';
import { getNameFromEmail, getReasonText } from '@/utils';

interface BlockedListTableProps {
  logs: BlockedSignupLog[];
  page: number;
  totalPages: number;
  total: number;
  pageSize: number;
  onPageChange: (page: number) => void;
  isSuperAdmin: boolean;
  unblockingLogId: number | null;
  loading: boolean;
  onUnblockClick: (logId: number) => void;
  searchQuery: string;
}

export const BlockedListTable: React.FC<BlockedListTableProps> = ({
  logs,
  page,
  totalPages,
  total,
  pageSize,
  onPageChange,
  isSuperAdmin,
  unblockingLogId,
  loading,
  onUnblockClick,
  searchQuery,
}) => {
  if (loading) {
    return (
      <div className="flex items-center justify-center h-32">
        <p className="text-sm text-gray-500">로딩 중...</p>
      </div>
    );
  }

  if (logs.length === 0) {
    return (
      <EmptyState
        message={searchQuery ? '검색 결과가 없습니다.' : '최근 28일 이내에 차단된 가입 요청이 없습니다.'}
      />
    );
  }

  return (
    <>
      <div className="overflow-x-auto border rounded-lg">
        <Table>
          <TableHeader>
            <TableRow>
              {/* 보안: 슈퍼관리자에게만 "ID" 및 "고객사" 컬럼 표시 */}
              {isSuperAdmin && <TableHead>ID</TableHead>}
              {isSuperAdmin && <TableHead>고객사</TableHead>}
              <TableHead>이메일</TableHead>
              <TableHead>이름</TableHead>
              <TableHead>도메인</TableHead>
              <TableHead>사유</TableHead>
              <TableHead>발생시간 (및 해지시간)</TableHead>
              <TableHead className="text-right">작업</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {logs.map((log) => (
              <TableRow key={log.id}>
                {/* 보안: 슈퍼관리자에게만 "ID" 및 "고객사" 컬럼 표시 */}
                {isSuperAdmin && (
                  <TableCell className="text-sm text-gray-600">
                    {log.id}
                  </TableCell>
                )}
                {isSuperAdmin && (
                  <TableCell className="text-sm">
                    {log.customer?.customer_name || '(미할당)'}
                  </TableCell>
                )}
                <TableCell className="text-sm font-medium">
                  {log.email || <span className="text-gray-400">알 수 없음</span>}
                </TableCell>
                <TableCell className="text-sm">
                  {log.email ? getNameFromEmail(log.email) : <span className="text-gray-400">알 수 없음</span>}
                </TableCell>
                <TableCell className="text-sm">{log.domain}</TableCell>
                <TableCell className="text-sm">
                  {log.reason ? getReasonText(log.reason) : <span className="text-gray-400">-</span>}
                </TableCell>
                <TableCell className="text-sm text-gray-500">
                  <div className="space-y-1">
                    <div>{formatDateTimeToKST(log.created_at)}</div>
                    {log.deleted_at && (
                      <div className="text-xs text-gray-400">
                        (차단해제: {formatDateTimeToKST(log.deleted_at)})
                      </div>
                    )}
                  </div>
                </TableCell>
                <TableCell className="text-right">
                  {!log.deleted_at && (
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => onUnblockClick(log.id)}
                      disabled={unblockingLogId === log.id || loading}
                    >
                      {unblockingLogId === log.id ? '처리 중...' : '차단해제'}
                    </Button>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      {/* 페이지네이션 */}
      <Pagination
        currentPage={page}
        totalPages={totalPages}
        totalItems={total}
        itemsPerPage={pageSize}
        onPageChange={onPageChange}
        showItemsPerPage={false}
        showTotal={true}
        className="mt-4"
      />
    </>
  );
};

